<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import BackendPagination from "@/components/BackendPagination.vue";

const blogCategories = ref([]);
const errorMessage = ref("");
const successMessage = ref("");
const loading = ref(true);
const showModal = ref(false);
const isEditing = ref(false);
const selectedBlogCategory = ref(null);

const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

const router = useRouter();
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage));

const fetchBlogCategories = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/blog_categories?page=${currentPage.value}`);
    blogCategories.value = response.data.member || [];
    totalItems.value = response.data.totalItems || 0;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Error al obtener categorías de blog.";
  } finally {
    loading.value = false;
  }
};

const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    await fetchBlogCategories();
  }
};

const openModal = () => {
  isEditing.value = false;
  selectedBlogCategory.value = { name: "" };
  showModal.value = true;
};

const openEditModal = (blogCategory) => {
  isEditing.value = true;
  selectedBlogCategory.value = {
    "@id": blogCategory["@id"],
    name: blogCategory.name || "",
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedBlogCategory.value = null;
  errorMessage.value = "";
};

const saveBlogCategory = async () => {
  try {
    if (!selectedBlogCategory.value.name) {
      errorMessage.value = "El nombre de la categoría es obligatorio.";
      return;
    }

    if (isEditing.value) {
      const id = selectedBlogCategory.value["@id"].replace(/^\/api/, "");
      await api.patch(id, {
        name: selectedBlogCategory.value.name,
      });
      successMessage.value = "Categoría de blog actualizada correctamente.";
    } else {
      const response = await api.post("/blog_categories", {
        name: selectedBlogCategory.value.name,
      });
      if (response?.data) {
        blogCategories.value.push(response.data);
      }
      successMessage.value = `Categoría de blog "${selectedBlogCategory.value.name}" agregada correctamente.`;
    }

    fetchBlogCategories();
    closeModal();

    setTimeout(() => {
      successMessage.value = "";
      errorMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al guardar categoría:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al guardar la categoría.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};

const deleteBlogCategory = async (blogCategory) => {
  if (!confirm(`¿Eliminar la categoría "${blogCategory.name}"?`)) return;

  try {
    const id = blogCategory["@id"].replace(/^\/api/, "");
    await api.delete(id);
    fetchBlogCategories();

    successMessage.value = `Categoría de blog "${blogCategory.name}" eliminada correctamente.`;

    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al eliminar categoría:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al eliminar la categoría.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};

onMounted(() => {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    router.push("/signin");
  } else {
    fetchBlogCategories();
  }
});
</script>




<template>
    <div class="py-4 container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header pb-0 d-flex">
              <h6>Categorías de Blog</h6>
              <argon-button color="primary" size="sm" class="ms-auto" @click="openModal">
                <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
                Agregar Categoría
              </argon-button>
            </div>
  
            <div class="card-body px-0 pt-0 pb-2">
              <div class="table-responsive p-0">
                <div v-if="loading" class="text-center py-3">
                  <p class="text-secondary">Cargando categorías...</p>
                </div>
  
                <argon-alert
                  v-if="successMessage"
                  color="alert-success"
                  icon="ni ni-check-bold"
                  class="mb-2 text-dark w-50 ms-3"
                >
                  {{ successMessage }}
                </argon-alert>
  
                <argon-alert
                  v-if="errorMessage && !showModal"
                  color="alert-danger"
                  icon="ni ni-fat-remove"
                  class="mb-2 text-dark w-50 ms-3"
                >
                  {{ errorMessage }}
                </argon-alert>
  
                <div
                  v-if="blogCategories.length === 0 && !loading && !errorMessage"
                  class="text-center py-3"
                >
                  <img
                    src="@/assets/img/illustrations/noData.png"
                    alt="No hay datos disponibles"
                    width="300"
                  />
                  <p class="text-secondary">No se encontraron categorías</p>
                </div>
  
                <table v-if="blogCategories.length > 0" class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Nombre
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="blogCategory in blogCategories" :key="blogCategory['@id']">
                      <td>
                        <div class="d-flex px-2 py-1">
                          <div class="d-flex flex-column justify-content-center">
                            <h6 class="mb-0 text-sm">{{ blogCategory.name }}</h6>
                          </div>
                        </div>
                      </td>
                      <td>
                        <font-awesome-icon
                          :icon="['fas', 'pen-to-square']"
                          class="me-3"
                          @click="openEditModal(blogCategory)"
                          style="cursor:pointer;"
                        />
                        <font-awesome-icon
                          :icon="['fas', 'trash']"
                          @click="deleteBlogCategory(blogCategory)"
                          style="cursor:pointer;"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
  
                <BackendPagination
                  :current-page="currentPage"
                  :total-pages="totalPages"
                  @change-page="changePage"
                />
              </div>
            </div>
          </div>
  
          <!-- Modal -->
          <teleport to="body">
            <div v-if="showModal" class="modal-overlay" @click="closeModal">
              <div class="modal-content" @click.stop>
                <h5 class="mb-3">{{ isEditing ? "Editar Categoría" : "Agregar Categoría" }}</h5>
                <argon-alert
                  v-if="showModal && errorMessage"
                  color="alert-danger"
                  icon="ni ni-fat-remove"
                  class="mb-3"
                >
                  {{ errorMessage }}
                </argon-alert>
                <div class="mb-3">
                  <label>Nombre de la Categoría</label>
                  <input v-model="selectedBlogCategory.name" class="form-control" type="text" required />
                </div>
                <div class="d-flex justify-content-end">
                  <argon-button color="secondary" class="me-2" @click="closeModal">Cancelar</argon-button>
                  <argon-button color="primary" @click="saveBlogCategory">
                    {{ isEditing ? "Guardar Cambios" : "Agregar" }}
                  </argon-button>
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>
    </div>
  </template>
  

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1051;
  width: 500px;
  max-width: 90%;
  position: relative;
}
</style>